// src/quality-management/components/CreateProjectModal.tsx
// Reescrito sobre o Modal genérico do UI kit e o formulário compartilhado de projeto

import React, { useState } from 'react';
import { CreateProjectRequest } from '../services/api/types';
import { useMutation } from '../hooks/useApi';
import { useEmployees, useManagers } from '../hooks/useDirectory';
import { apiService } from '../services/api';
import { Modal } from '../../shared/components/ui/Modal';
import { Button } from '../../shared/components/ui/Button';
import ProjectFormFields from './project-form/ProjectFormFields';
import { ProjectFormState, EMPTY_PROJECT_FORM, buildProjectPayload } from './project-form/projectForm';

interface CreateProjectModalProps {
  onClose: () => void;
  onProjectCreated: () => void;
}

const FORM_ID = 'create-project-form';

const CreateProjectModal: React.FC<CreateProjectModalProps> = ({ onClose, onProjectCreated }) => {
  const [form, setForm] = useState<ProjectFormState>(EMPTY_PROJECT_FORM);
  const [error, setError] = useState('');

  const update = (patch: Partial<ProjectFormState>) => setForm((prev) => ({ ...prev, ...patch }));

  const { data: employees, loading: loadingEmployees } = useEmployees();
  const { data: managers } = useManagers();

  const { mutate: createProject, loading: isSubmitting } = useMutation(
    (data: CreateProjectRequest) => apiService.projects.createProject(data),
    {
      onSuccess: () => onProjectCreated(),
      onError: (e) => setError(e.message || 'Erro ao criar projeto. Tente novamente.'),
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Nome do projeto é obrigatório');
      return;
    }
    setError('');
    await createProject(buildProjectPayload(form));
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Modal
      isOpen
      onClose={handleClose}
      title="Novo projeto"
      description="Comece pelo essencial. Os dados de gestão podem ser preenchidos depois."
      size="xl"
      closeOnBackdrop={!isSubmitting}
      data-testid="create-project-modal"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            loading={isSubmitting}
            disabled={isSubmitting || !form.name.trim()}
            data-testid="project-save"
          >
            Criar projeto
          </Button>
        </>
      }
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <ProjectFormFields
          form={form}
          update={update}
          managers={managers || []}
          employees={employees || []}
          loadingEmployees={loadingEmployees}
          disabled={isSubmitting}
          error={error}
        />
      </form>
    </Modal>
  );
};

export default CreateProjectModal;
